/**
 * UniversalTableSearch - Universal search, filter, and grouping component
 *
 * Provides a standardized way to add search, filtering, and grouping
 * to any table with configurable options.
 */

import { useMemo } from 'react';
import { useSession } from '@clerk/clerk-react';
import { FilterBar } from 'shared/components/filterBar';
import { BulkAction } from 'shared/components/filterBar/types';
import { useApiFilterOptions } from '../hooks/useApiFilterOptions';
import { FileArrowDown } from '@phosphor-icons/react';
import ButtonNew from 'components/buttonNew/buttonNew';

export interface UniversalFilterSection {
  key: string;
  label: string;
  options: Array<{ key: string; label: string }>;
  color?: 'moss' | 'brick' | 'yellow' | 'neutral';
  multiSelect?: boolean;
  singleSelect?: boolean;

  // OPTION 1: Extract from table data (synchronous, limited to loaded data)
  // Use when: Filter options should only show what's currently in the table
  // Example: Labels extracted from loaded files
  extractOptionsFromData?: (rows: any[]) => Array<{ key: string; label: string }>;

  // OPTION 2: Fetch from API (asynchronous, complete dataset)
  // Use when: Need to filter by entities not yet loaded in table (e.g., lazy-loaded tables)
  // Example: All buildings from database for filtering files by building
  fetchFromAPI?: {
    // API endpoint to fetch options from (relative to API_URL or full URL)
    endpoint: string;
    // Optional: Transform function to convert API response to option format
    // If not provided, assumes API returns string[] and maps to { key: string, label: string }
    transform?: (data: any) => Array<{ key: string; label: string }>;
    // Optional: Request method (default: GET)
    method?: 'GET' | 'POST';
    // Optional: Request body for POST requests
    body?: any;
    // Optional: Cache duration in milliseconds (default: 5 minutes)
    cacheDuration?: number;
    // Optional: Refetch interval (for live data)
    refetchInterval?: number;
  };

  // OPTION 3: Pre-fetched async options (managed by parent component)
  // Use when: Parent component manages fetching (legacy support)
  asyncOptions?: Array<{ key: string; label: string }>;
}

export interface UniversalGroupByOption {
  key: string;
  label: string;
}

export interface UniversalTableSearchProps<T> {
  // Data
  rows: T[];
  // Optional: Provide a different dataset for extracting filter options
  // Use when: Table rows are filtered but filters should show options from full dataset
  rowsForExtraction?: T[];

  // Search
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;

  // Filters
  filterSections: UniversalFilterSection[];
  activeFilters: Record<string, string[]>;
  onFilterChange: (key: string, values: string[]) => void;

  // Group By
  groupByOptions?: UniversalGroupByOption[];
  selectedGroupBy?: string;
  onGroupByChange?: (groupBy: string) => void;

  // Bulk Selection
  selectedCount?: number;
  onClearSelection?: () => void;
  bulkActions?: BulkAction[];

  // Saved Searches
  savedSearches?: Array<{
    id: string;
    name: string;
    filters?: any;
    params?: string;
  }>;
  onSaveCurrentSearch?: (name?: string) => void;
  onApplySavedSearch?: (search: any) => void;

  // URL Synchronization
  syncWithURL?: boolean;
  urlParamPrefix?: string;
  onFiltersFromURL?: (filters: any) => void;

  // Loading
  isLoading?: boolean;
  isLoadingMore?: boolean;
  lazyLoadingContent?: React.ReactNode;

  // Display
  showSpacer?: boolean; // Show spacer div at bottom (default: true)

  // Excel Export
  excelExportConfig?: {
    filename?: string;
    sheetName?: string;
    enabled?: boolean;
    buttonText?: string;
  };
  onExcelExport?: (selectedRowsOnly: boolean) => void;
  isExporting?: boolean;
}

export const UniversalTableSearch = <T extends { [key: string]: any }>({
  rows,
  rowsForExtraction,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Zoeken...',
  filterSections,
  activeFilters,
  onFilterChange,
  groupByOptions,
  selectedGroupBy = 'none',
  onGroupByChange,
  selectedCount,
  onClearSelection,
  bulkActions,
  savedSearches,
  onSaveCurrentSearch,
  onApplySavedSearch,
  syncWithURL = false,
  urlParamPrefix = '',
  onFiltersFromURL,
  isLoading = false,
  isLoadingMore = false,
  lazyLoadingContent,
  showSpacer = true,
  excelExportConfig,
  onExcelExport,
  isExporting = false,
}: UniversalTableSearchProps<T>) => {
  const { session } = useSession();

  // Fetch API-based filter options for sections that have fetchFromAPI configured
  const apiFilterResults = filterSections
    .filter(section => section.fetchFromAPI)
    .map(section => ({
      key: section.key,
      ...useApiFilterOptions(section.fetchFromAPI || null, session)
    }));

  // Build filter chips from active filters
  const filterChips = useMemo(() => {
    const chips: Array<{
      key: string;
      label: string;
      category: string;
      onRemove: () => void;
      color: 'moss' | 'brick' | 'yellow' | 'neutral';
    }> = [];

    Object.entries(activeFilters).forEach(([filterKey, values]) => {
      const section = filterSections.find((s) => s.key === filterKey);
      if (!section) return;

      values.forEach((value) => {
        // Find the label for the value
        const option = section.options.find((opt) => opt.key === value);
        const displayLabel = option ? option.label : value;

        chips.push({
          key: `${filterKey}-${value}`,
          label: displayLabel,
          category: section.label,
          onRemove: () => {
            const newValues = values.filter((v) => v !== value);
            onFilterChange(filterKey, newValues);
          },
          color: section.color || 'neutral',
        });
      });
    });

    return chips;
  }, [activeFilters, filterSections, onFilterChange]);

  // Build filter sections with dynamic options
  // Priority system:
  // 1. fetchFromAPI (highest) - Fetch all options from API endpoint
  // 2. asyncOptions - Pre-fetched options managed by parent
  // 3. extractOptionsFromData - Extract from currently loaded table data
  // 4. options (lowest) - Static options defined in configuration
  const processedFilterSections = useMemo(() => {
    return filterSections.map((section) => {
      let options = section.options;

      // Priority 1: Use API-fetched options if available
      if (section.fetchFromAPI) {
        const apiResult = apiFilterResults.find(r => r.key === section.key);
        if (apiResult && apiResult.options.length > 0) {
          options = apiResult.options;
        }
      }
      // Priority 2: Use cached async options if available
      else if (section.asyncOptions && section.asyncOptions.length > 0) {
        options = section.asyncOptions;
      }
      // Priority 3: If extractOptionsFromData is provided, use it to get dynamic options from table data
      else if (section.extractOptionsFromData) {
        // Use rowsForExtraction if provided, otherwise fall back to rows
        options = section.extractOptionsFromData(rowsForExtraction || rows);
      }

      return {
        key: section.key,
        label: section.label,
        options,
        selectedValues: activeFilters[section.key] || [],
        onUpdate: (values: string[]) => onFilterChange(section.key, values),
        color: section.color || ('neutral' as const),
        singleSelect: section.singleSelect,
      };
    });
  }, [filterSections, rows, activeFilters, onFilterChange, apiFilterResults]);

  // Excel export button - only show when rows are selected
  const excelExportButton = excelExportConfig?.enabled !== false && onExcelExport && selectedCount && selectedCount > 0 ? (
    <ButtonNew
      variant="secondaryNeutral"
      onClick={() => onExcelExport(true)} // Always export selected when button is visible
      isDisabled={isExporting}
      icon={<FileArrowDown size={16} />}
      label={isExporting ? 'Exporteren...' : `${selectedCount} naar Excel exporteren`}
    />
  ) : null;

  // Determine right content: lazy loading indicator takes priority over excel export
  const rightContent = isLoadingMore && lazyLoadingContent ? lazyLoadingContent : excelExportButton;

  return (
    <FilterBar
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder={searchPlaceholder}
      filterChips={filterChips}
      selectedCount={selectedCount}
      onClearSelection={onClearSelection}
      bulkActions={bulkActions}
      filterSections={processedFilterSections}
      groupByOptions={groupByOptions}
      selectedGroupBy={selectedGroupBy}
      onGroupByChange={onGroupByChange}
      savedSearches={savedSearches}
      onSaveCurrentSearch={onSaveCurrentSearch}
      onApplySavedSearch={onApplySavedSearch}
      isLoading={isLoading}
      showSpacer={showSpacer}
      syncWithURL={syncWithURL}
      urlParamPrefix={urlParamPrefix}
      onFiltersFromURL={onFiltersFromURL}
      rightContent={rightContent}
    />
  );
};
